import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, limit, getDocs, startAfter } from 'firebase/firestore';

import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';
import CommentSection from './CommentSection';
import useOnScreen from '../hooks/useOnScreen';
import './Moments.css';

const MemoizedReelItem = React.memo(({ moment, onVisible, ...props }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.5 });

    useEffect(() => {
        if (isVisible) {
            onVisible(props.index);
        }
    }, [isVisible, onVisible, props.index]);

    return (
        <div ref={ref} className="reel-item">
            <ReelItem moment={moment} {...props} />
        </div>
    );
});

const ReelItem = ({ moment, isActive, onLike, onAmplify, onUserSelect, isMuted, onMuteToggle }) => {
    const { t } = useTranslation();
    const { LucideIcons, userId } = useAppContext();
    const isOwner = moment.authorId === userId;
    const [showComments, setShowComments] = useState(false);
    const videoRef = useRef(null);
    const reelRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showPlaybackIcon, setShowPlaybackIcon] = useState(false);
    const [isIntersecting, setIsIntersecting] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsIntersecting(entry.isIntersecting);
            },
            { rootMargin: "200px" }
        );
        if (reelRef.current) {
            observer.observe(reelRef.current);
        }
        return () => {
            if (reelRef.current) {
                observer.unobserve(reelRef.current);
            }
        };
    }, []);

    const handleVideoClick = () => {
        if (moment.isOptimistic) return; // Don't allow play/pause on optimistic moments
        setIsPlaying(prev => !prev);
        setShowPlaybackIcon(true);
        setTimeout(() => setShowPlaybackIcon(false), 600);
    };

    useEffect(() => {
        setIsPlaying(isActive);
    }, [isActive]);

    useEffect(() => {
        if (videoRef.current) {
            if (isPlaying && isIntersecting) {
                videoRef.current.play().catch(error => {
                    console.error("Video autoplay was prevented:", error);
                    setIsPlaying(false);
                });
            } else {
                videoRef.current.pause();
            }
        }
    }, [isPlaying, isIntersecting]);

    const renderPlayer = () => {
        if (moment.isLoading) {
            return <LoadingSpinner />;
        }
        // --- OPTIMISTIC UI ---
        if (isOwner && moment.moderationStatus === 'uploading') {
            return (
                <>
                    <video
                        src={moment.mediaUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover opacity-50"
                    ></video>
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white text-center p-4">
                        <div className="action-spinner mb-4"></div>
                        <p className="font-bold text-lg">Uploading...</p>
                        <p className="text-sm text-gray-300">Your Moment is being uploaded and will be processed shortly.</p>
                    </div>
                </>
            );
        }
        if (isOwner && ['pending', 'review_needed'].includes(moment.moderationStatus)) {
            return (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white text-center p-4">
                    <LucideIcons.ShieldCheck size={48} className="text-blue-400 mb-4 animate-pulse" />
                    <p className="font-bold text-lg">{t('myMomentsPage.underReview')}</p>
                    <p className="text-sm text-gray-400">{t('myMomentsPage.reviewMessage')}</p>
                </div>
            );
        }
        if (moment.moderationStatus === 'rejected') {
            return (
                <div className="w-full h-full flex flex-col items-center justify-center bg-red-900/50 text-white text-center p-4">
                    <LucideIcons.ShieldOff size={48} className="text-red-400 mb-4" />
                    <p className="font-bold text-lg">{t('myMomentsPage.contentRemoved')}</p>
                    <p className="text-sm text-gray-300">{t('myMomentsPage.violationMessage')}</p>
                </div>
            );
        }
        if (moment.moderationStatus === 'approved' && moment.mediaUrl) {
            return (
                <video
                    ref={videoRef}
                    key={moment.id}
                    src={isIntersecting ? moment.mediaUrl : ''}
                    loop
                    muted={isMuted}
                    playsInline
                    className="w-full h-full object-cover"
                ></video>
            );
        }
        return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-black text-white text-center p-4">
                <LucideIcons.AlertTriangle size={48} className="text-yellow-500 mb-4" />
                <p className="font-bold">{t('myMomentsPage.contentUnavailable')}</p>
            </div>
        );
    };

    return (
        <div ref={reelRef} className="w-full h-full" onClick={handleVideoClick}>
            <div className="reel-player-wrapper">{renderPlayer()}</div>

            {moment.moderationStatus === 'approved' && moment.mediaUrl && !moment.isOptimistic && (
                <>
                    <div className={`reel-playback-icon ${showPlaybackIcon ? 'visible' : ''}`}>
                        {isPlaying ? <LucideIcons.Pause size={64} /> : <LucideIcons.Play size={64} />}
                    </div>
                    <div className="reel-overlay"></div>
                    <div className="reel-ui-container">
                        <div className="reel-details">
                            <div className="author-info" onClick={(e) => { e.stopPropagation(); onUserSelect(moment.authorId); }}>
                                <img src={moment.authorPhotoURL || "https://placehold.co/40x40/AEC6CF/FFFFFF?text=U"} alt={moment.authorName} className="w-10 h-10 rounded-full object-cover border-2 border-white" />
                                <span>{moment.authorName}</span>
                            </div>
                            {moment.content && <p className="caption">{moment.content}</p>}
                        </div>
                        <div className="reel-actions">
                            <button onClick={(e) => { e.stopPropagation(); onMuteToggle(); }} className="reel-action-button">
                                {isMuted ? <LucideIcons.VolumeX size={24} /> : <LucideIcons.Volume2 size={24} />}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); onLike(moment.id, moment.authorId); }} className="reel-action-button">
                                <LucideIcons.Heart size={32} fill={moment.likes?.includes(userId) ? '#ef4444' : 'transparent'} color={moment.likes?.includes(userId) ? '#ef4444' : 'white'} />
                                <span className="count">{moment.likesCount || 0}</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setShowComments(!showComments); }} className="reel-action-button">
                                <LucideIcons.MessageCircle size={32} color="white" />
                                <span className="count">{moment.commentsCount || 0}</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); onAmplify(moment.id); }} className="reel-action-button">
                                <LucideIcons.Flame size={32} color="#f59e0b" />
                                <span className="count">{moment.echoesInvested || 0}</span>
                            </button>
                        </div>
                    </div>
                    {showComments && (
                        <div className="comments-panel-overlay" onClick={() => setShowComments(false)}>
                            <div className={`comments-panel ${showComments ? 'open' : ''}`} onClick={e => e.stopPropagation()}>
                                <div className="comments-panel-header">
                                    <h3>{t('myMomentsPage.comments')}</h3>
                                    <button onClick={() => setShowComments(false)} className="p-2 text-gray-400 hover:text-white"><LucideIcons.X size={24} /></button>
                                </div>
                                <div className="comments-panel-body">
                                    <CommentSection entryId={moment.id} contentType="moment" />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

function MyMomentsPage() {
    const { t } = useTranslation();
    const { appFunctions, setMessage, handleUserSelect, showConfirmation, userId, handlePageChange, LucideIcons, db, appId, optimisticMoments } = useAppContext();
    const [moments, setMoments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMuted, setIsMuted] = useState(true);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const isFetching = useRef(false);
    const POSTS_PER_PAGE = 5;

    const combinedMoments = useMemo(() => {
        const optimisticIds = new Set(optimisticMoments.map(m => m.id));
        const fetchedFiltered = moments.filter(m => !optimisticIds.has(m.id));
        return [...optimisticMoments, ...fetchedFiltered];
    }, [optimisticMoments, moments]);

    const fetchMyMoments = useCallback(async (loadMore = false) => {
        if (isFetching.current || (loadMore && !hasMore)) return;

        isFetching.current = true;
        if (!loadMore) {
            setIsLoading(true);
        }
        setError(null);

        try {
            const momentsRef = collection(db, `artifacts/${appId}/public/data/moments`);
            let q = query(
                momentsRef,
                where('authorId', '==', userId),
                // We fetch all statuses now and filter on the client to ensure optimistic ones aren't prematurely removed
                orderBy('timestamp', 'desc'),
                limit(POSTS_PER_PAGE)
            );

            if (loadMore && lastVisible) {
                q = query(q, startAfter(lastVisible));
            }

            const documentSnapshots = await getDocs(q);
            const newMoments = documentSnapshots.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            setLastVisible(documentSnapshots.docs[documentSnapshots.docs.length - 1]);
            setMoments(prev => loadMore ? [...prev, ...newMoments] : newMoments);
            setHasMore(newMoments.length === POSTS_PER_PAGE);

        } catch (err) {
            setError(t('myMomentsPage.loadError'));
            setMessage(t('myMomentsPage.loadErrorLong', { message: err.message }));
        } finally {
            setIsLoading(false);
            isFetching.current = false;
        }
    }, [db, appId, userId, hasMore, lastVisible, t, setMessage]);


    useEffect(() => {
        if (userId) {
            fetchMyMoments(false);
        }
    }, [userId, fetchMyMoments]);

    useEffect(() => {
        if (!isLoading && hasMore && combinedMoments.length > 0 && currentIndex >= combinedMoments.length - 2) {
            fetchMyMoments(true);
        }
    }, [currentIndex, combinedMoments.length, hasMore, isLoading, fetchMyMoments]);

    const handleLike = useCallback(async (momentId, authorId) => {
        // Optimistic update for likes
        const updateLikes = (prev) => prev.map(m => {
            if (m.id === momentId) {
                const hasLiked = m.likes?.includes(userId);
                return { ...m, likes: hasLiked ? m.likes.filter(id => id !== userId) : [...(m.likes || []), userId], likesCount: hasLiked ? (m.likesCount || 1) - 1 : (m.likesCount || 0) + 1 };
            }
            return m;
        });
        setMoments(updateLikes);

        try {
            const updateReaction = httpsCallable(appFunctions, 'updateReaction');
            await updateReaction({ entryId: momentId, authorId, reaction: 'like', contentType: 'moment' });
        } catch (error) {
            console.error("Error syncing 'like' reaction:", error);
            setMessage(t('myMomentsPage.likeError'));
            // Revert optimistic update on error
            setMoments(updateLikes);
        }
    }, [appFunctions, setMessage, userId, t]);

    const handleAmplify = useCallback((momentId) => {
        showConfirmation({
            message: t('myMomentsPage.amplifyConfirmation'),
            onConfirm: async () => {
                const amplifyMoment = httpsCallable(appFunctions, 'amplifyMoment');
                try {
                    await amplifyMoment({ momentId: momentId, amount: 10 });
                    setMessage(t('myMomentsPage.amplifySuccess'));
                } catch (error) { setMessage(`${t('myMomentsPage.amplifyFailed')}${error.message}`); }
            }
        });
    }, [appFunctions, setMessage, showConfirmation, t]);

    const handleVisibilityChange = useCallback((index) => {
        setCurrentIndex(index);
    }, []);

    if (isLoading && combinedMoments.length === 0) return <LoadingSpinner message={t('myMomentsPage.loading')} />;
    if (error) return <div className="fixed inset-0 z-50 bg-black flex items-center justify-center text-red-400">{error}</div>;

    return (
        <>
            <button 
                onClick={() => window.history.back()} 
                className="fixed top-4 left-4 z-50 bg-black/50 text-white p-2 rounded-full hover:bg-black/75 transition-colors"
                aria-label="Go back"
            >
                <LucideIcons.ChevronLeft size={28} />
            </button>
            <div className="moments-viewer">
                {combinedMoments.length > 0 ? (
                    combinedMoments.map((moment, index) => (
                        <MemoizedReelItem
                            key={moment.id}
                            index={index}
                            moment={moment}
                            isActive={index === currentIndex}
                            onLike={handleLike}
                            onAmplify={handleAmplify}
                            onUserSelect={handleUserSelect}
                            isMuted={isMuted}
                            onMuteToggle={() => setIsMuted(prev => !prev)}
                            onVisible={handleVisibilityChange}
                        />
                    ))
                ) : (
                    <div className="reel-item text-white text-center flex items-center justify-center">
                        <div className="p-8 bg-gray-800/50 rounded-lg max-w-sm">
                            <LucideIcons.VideoOff size={48} className="mx-auto mb-4 text-purple-400" />
                            <h3 className="text-xl font-bold">{t('myMomentsPage.emptyTitle')}</h3>
                            <p className="mt-2 mb-4 text-gray-300">{t('myMomentsPage.emptyMessage')}</p>
                            <button onClick={() => handlePageChange('createMoment')} className="small-action-button bg-purple-600 hover:bg-purple-700 text-base px-6 py-3">
                                {t('myMomentsPage.createFirstMoment')}
                            </button>
                        </div>
                    </div>
                )}
                {isLoading && combinedMoments.length > 0 && <LoadingSpinner message={t('myMomentsPage.loadingMore')} />}
            </div>
        </>
    );
}

export default MyMomentsPage;